import React, { useMemo } from 'react';

interface UserPaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  showingFrom: number;
  showingTo: number;
  onPageChange: (page: number) => void;
}

// Generate visible page numbers with ellipsis (max 5 visible)
function getPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | '...')[] = [1];

  if (current > 3) pages.push('...');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let i = start; i <= end; i++) pages.push(i);

  if (current < total - 2) pages.push('...');

  pages.push(total);
  return pages;
}

const UserPagination: React.FC<UserPaginationProps> = ({ currentPage, totalPages, totalCount, showingFrom, showingTo, onPageChange }) => {
  const pageNumbers = useMemo(() => getPageNumbers(currentPage, totalPages), [currentPage, totalPages]);

  if (totalCount === 0) return null;

  return (
    <div className='flex flex-col items-center justify-between gap-3 sm:flex-row'>
      {/* Showing X-Y of Z */}
      <span className='text-sm text-gray-500 dark:text-dark-text-secondary'>
        {showingFrom}–{showingTo} / {totalCount}
      </span>

      {/* Page buttons */}
      <div className='flex items-center gap-1'>
        {/* Previous */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className='rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-dark-text-secondary dark:hover:bg-dark-background-tertiary'
        >
          <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
          </svg>
        </button>

        {/* Page numbers */}
        {pageNumbers.map((page, i) =>
          page === '...' ? (
            <span key={`ellipsis-${i}`} className='px-2 text-sm text-gray-400 dark:text-dark-text-secondary'>
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`min-w-[32px] rounded px-2 py-1 text-sm transition-colors ${
                page === currentPage
                  ? 'bg-primary text-white'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-dark-text dark:hover:bg-dark-background-tertiary'
              }`}
            >
              {page}
            </button>
          )
        )}

        {/* Next */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className='rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-dark-text-secondary dark:hover:bg-dark-background-tertiary'
        >
          <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default UserPagination;
